import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { formatDate, getDateParts } from "../lib/dateUtils";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, upcoming, past

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await api.getEvents();
        setEvents(data || []);
      } catch {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const today = new Date().toISOString().split('T')[0];
  
  const filteredEvents = events.filter(event => {
    if (filter === "upcoming") return event.date >= today;
    if (filter === "past") return event.date < today;
    return true;
  }).sort((a, b) => {
    // Upcoming first, then past
    const aIsUpcoming = a.date >= today;
    const bIsUpcoming = b.date >= today;
    if (aIsUpcoming && !bIsUpcoming) return -1;
    if (!aIsUpcoming && bIsUpcoming) return 1;
    // Within same category, sort by date
    return aIsUpcoming 
      ? a.date.localeCompare(b.date) 
      : b.date.localeCompare(a.date);
  });

  const isUpcoming = (dateStr) => dateStr >= today;

  if (loading) {
    return (
      <div className="min-h-screen bg-demolition-black flex items-center justify-center">
        <div className="animate-pulse text-demolition-red font-russo text-2xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="bg-demolition-black min-h-screen" data-testid="events-page">
      {/* Header */}
      <section className="relative py-24 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 steel-mesh opacity-20" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <Calendar className="w-16 h-16 text-demolition-red mx-auto mb-6" />
            <h1 
              className="font-russo text-5xl sm:text-6xl lg:text-7xl text-white uppercase tracking-tighter mb-4"
              data-testid="events-title"
            >
              EVENTS
            </h1>
            <p className="font-teko text-xl sm:text-2xl text-demolition-chrome uppercase tracking-widest">
              Meet Demolition in Person
            </p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="px-6 md:px-12 bg-demolition-paper border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-0" data-testid="events-filter">
            {[
              { value: "all", label: "All Events" },
              { value: "upcoming", label: "Upcoming" },
              { value: "past", label: "Past Events" },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => setFilter(tab.value)}
                data-testid={`filter-${tab.value}`}
                className={`px-6 py-4 font-teko text-lg uppercase tracking-wider transition-colors border-b-4 ${
                  filter === tab.value
                    ? "border-demolition-red text-white"
                    : "border-transparent text-white/50 hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-16 px-6 md:px-12 bg-demolition-paper" data-testid="events-list">
        <div className="max-w-7xl mx-auto">
          {filteredEvents.length > 0 ? (
            <div className="space-y-6">
              {filteredEvents.map((event) => (
                <Link
                  key={event.id}
                  to={`/events/${event.slug}`}
                  className={`block group bg-demolition-subtle border border-white/10 hover:border-demolition-red/50 transition-colors ${
                    !isUpcoming(event.date) ? 'opacity-60' : ''
                  }`}
                  data-testid={`event-item-${event.slug}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Date Block */}
                    <div className={`md:w-48 p-6 flex md:flex-col items-center justify-center gap-4 md:gap-2 border-b md:border-b-0 md:border-r border-white/10 ${
                      isUpcoming(event.date) ? 'bg-demolition-red' : 'bg-demolition-subtle'
                    }`}>
                      <span className="font-russo text-4xl md:text-5xl text-white">
                        {getDateParts(event.date).day}
                      </span>
                      <div className="text-center">
                        <span className="font-teko text-lg text-white/80 uppercase block">
                          {getDateParts(event.date).month}
                        </span>
                        <span className="font-mono text-sm text-white/60">
                          {getDateParts(event.date).year}
                        </span>
                      </div>
                    </div>

                    {/* Event Info */}
                    <div className="flex-1 p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            {isUpcoming(event.date) ? (
                              <span className="px-3 py-1 bg-demolition-red text-white font-teko text-sm uppercase tracking-wider">
                                Upcoming
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-white/10 text-white/50 font-teko text-sm uppercase tracking-wider">
                                Past Event
                              </span>
                            )}
                          </div>
                          <h3 className="font-russo text-2xl md:text-3xl text-white uppercase tracking-tighter group-hover:text-demolition-red transition-colors">
                            {event.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-4 text-white/50">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span className="font-mono text-sm">{event.location}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span className="font-mono text-sm">{formatDate(event.date)}</span>
                            </div>
                          </div>
                          {event.description && (
                            <p className="font-mono text-white/60 text-sm line-clamp-2">
                              {event.description}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="w-6 h-6 text-white/30 group-hover:text-demolition-red transition-colors flex-shrink-0" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <Calendar className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="font-mono text-white/50">No events found.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
